import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Circle, ListTodo, StickyNote, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { DatePicker } from "@/components/common/DatePicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";
import { useFormat } from "@/lib/format";
import { cn } from "@/lib/utils";

export const TaskRow = ({ t, company, onChange, showTarget }) => {
  const { date, money } = useFormat();
  const canEdit = company.my_role !== "viewer";
  const base = `/business/companies/${company.id}/tasks/${t.id}`;
  const toggle = async () => { try { await api.put(base, { done: !t.done }); onChange(); } catch (e) { toast.error(formatApiError(e)); } };
  const del = async () => { try { await api.delete(base); onChange(); } catch (e) { toast.error(formatApiError(e)); } };
  const action = t.kind === "action";
  return (
    <li className="flex items-start gap-2 text-xs" data-testid={`task-${t.id}`}>
      {action ? <button type="button" onClick={toggle} disabled={!canEdit} aria-label={t.done ? "Rouvrir" : "Marquer comme fait"} className="mt-0.5 text-primary" data-testid={`task-toggle-${t.id}`}>
        {t.done ? <CheckCircle2 className="h-4 w-4 text-success" /> : <Circle className="h-4 w-4" />}</button> : <StickyNote className="mt-0.5 h-4 w-4 shrink-0 text-butter" />}
      <div className="min-w-0 flex-1">
        <p className={cn(t.done && "text-muted-foreground line-through")}>{t.rebill_to && <b className="mr-1 rounded-full bg-primary/10 px-2 py-0.5 text-primary">Refacturer à {t.rebill_to}</b>}{t.text}</p>
        <p className="text-[11px] text-muted-foreground">{t.auto ? "Ajouté par CASHMY" : t.author} · {date(t.created_at.slice(0, 10))}{t.due_date && ` · avant le ${date(t.due_date)}`}
          {showTarget && t.target && <> · <b>{t.target.label}</b>{t.target.amount != null && ` ${money(t.target.amount, { maximumFractionDigits: 2 })}`}</>}</p>
      </div>
      {canEdit && <button type="button" onClick={del} aria-label="Supprimer" className="text-muted-foreground hover:text-destructive" data-testid={`task-delete-${t.id}`}><Trash2 className="h-3.5 w-3.5" /></button>}
    </li>
  );
};

const TaskForm = ({ company, type, id, onDone }) => {
  const [v, setV] = useState({ kind: "note", text: "", rebill_to: "", due_date: "" });
  const send = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/business/companies/${company.id}/tasks`, { target_type: type, target_id: id, kind: v.kind, text: v.text, rebill_to: v.kind === "action" ? v.rebill_to || null : null, due_date: v.kind === "action" ? v.due_date || null : null });
      setV({ ...v, text: "", rebill_to: "", due_date: "" }); onDone();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <form onSubmit={send} className="space-y-2" data-testid={`task-form-${id}`}>
      <div className="flex flex-wrap gap-1.5">{[["note", "Note", StickyNote], ["action", "Action à faire", ListTodo]].map(([k, l, I]) => (
        <button key={k} type="button" onClick={() => setV({ ...v, kind: k })} data-testid={`task-kind-${k}-${id}`}
          className={cn("inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors", v.kind === k ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-muted")}><I className="h-3 w-3" />{l}</button>))}</div>
      <Input value={v.text} onChange={(e) => setV({ ...v, text: e.target.value })} placeholder={v.kind === "note" ? "Ex. Achat pour le chantier Martin" : "Ex. Envoyer la facture de refacturation"} required minLength={2} maxLength={500} className="h-9 text-sm" data-testid={`task-text-${id}`} />
      {v.kind === "action" && <div className="grid gap-2 sm:grid-cols-2">
        <Input value={v.rebill_to} onChange={(e) => setV({ ...v, rebill_to: e.target.value })} placeholder="Refacturer à (entreprise, facultatif)" maxLength={120} className="h-9 text-sm" data-testid={`task-rebill-${id}`} />
        <DatePicker value={v.due_date} onChange={(e) => setV({ ...v, due_date: e.target.value })} fromDate={new Date()} placeholder="À faire avant le (facultatif)" data-testid={`task-due-${id}`} /></div>}
      <Button type="submit" size="sm" className="h-8 rounded-full" data-testid={`task-submit-${id}`}>Ajouter</Button>
    </form>
  );
};

export const ItemNotes = ({ company, type, id }) => {
  const qc = useQueryClient();
  const key = ["biz-tasks", company.id, type, id];
  const { data = [] } = useQuery({ queryKey: key, queryFn: () => api.get(`/business/companies/${company.id}/tasks`, { params: { target_type: type, target_id: id } }).then((r) => r.data) });
  const refresh = () => { qc.invalidateQueries({ queryKey: key }); qc.invalidateQueries({ queryKey: ["biz-open-actions"] }); qc.invalidateQueries({ queryKey: ["biz-debts"] }); };
  return (
    <div className="space-y-3 rounded-xl bg-muted/40 p-3" data-testid={`item-notes-${id}`}>
      {data.length > 0 ? <ul className="space-y-2">{data.map((t) => <TaskRow key={t.id} t={t} company={company} onChange={refresh} />)}</ul>
        : <p className="text-xs text-muted-foreground">Aucune note pour l'instant.</p>}
      {company.my_role !== "viewer" && <TaskForm company={company} type={type} id={id} onDone={refresh} />}
    </div>
  );
};

export const OpenActions = ({ company }) => {
  const qc = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ["biz-open-actions", company.id], queryFn: () => api.get(`/business/companies/${company.id}/tasks`, { params: { open_actions: true } }).then((r) => r.data) });
  if (!data.length) return null;
  const refresh = () => { qc.invalidateQueries({ queryKey: ["biz-open-actions"] }); qc.invalidateQueries({ queryKey: ["biz-tasks"] }); qc.invalidateQueries({ queryKey: ["biz-debts"] }); };
  return (
    <section className="card-surface space-y-3 border-primary/30 p-5" data-testid="open-actions">
      <h2 className="flex items-center gap-2 font-bold"><ListTodo className="h-4 w-4 text-primary" /> Actions à faire <span className="rounded-full bg-primary px-2 text-xs text-primary-foreground">{data.length}</span></h2>
      <ul className="space-y-2.5">{data.map((t) => <TaskRow key={t.id} t={t} company={company} onChange={refresh} showTarget />)}</ul>
    </section>
  );
};
